import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { scannerApi, apiMessage, extractData, extractArray } from 'shared/api/scanner'
import { useAuth } from 'context/AuthContext'
import { SCANNER_MATRIX_KEYS, MATRIX_LABELS, canManageStaff } from 'shared/util/permissions'
import { PermissionEmpty, Segmented } from 'shared/components/ui/primitives'
import Button from 'shared/components/ui/button'
import Input from 'shared/components/ui/input'
import Label from 'shared/components/ui/label.js'
import { IconBack } from 'shared/components/icons'

function permissionId(item) {
  return item?._id || item?.iPermissionId
}

function permissionKey(item) {
  return item?.sKey || item?.sPermission || item?.sName
}

function StaffForm() {
  const { staffId } = useParams()
  const editing = Boolean(staffId)
  const navigate = useNavigate()
  const { user } = useAuth()
  const [saving, setSaving] = useState(false)
  const [events, setEvents] = useState([])
  const [catalog, setCatalog] = useState([])
  const [roles, setRoles] = useState([])
  const [roleType, setRoleType] = useState('SCANNER_USER')
  const [form, setForm] = useState({
    sName: '',
    sEmail: '',
    sMobile: '',
    sCountryCode: '91'
  })
  const [matrix, setMatrix] = useState({})

  const scannerCatalog = useMemo(
    () =>
      (catalog || []).filter((item) => {
        const key = permissionKey(item)
        return SCANNER_MATRIX_KEYS.includes(key)
      }),
    [catalog]
  )

  const mappedColumns = SCANNER_MATRIX_KEYS.map((key) => ({
    key,
    label: MATRIX_LABELS[key],
    id: permissionId(scannerCatalog.find((item) => permissionKey(item) === key))
  })).filter((col) => col.id)

  const columns =
    mappedColumns.length > 0
      ? mappedColumns
      : (catalog || []).slice(0, 8).map((item) => ({
          key: permissionKey(item) || permissionId(item),
          label: MATRIX_LABELS[permissionKey(item)] || permissionKey(item) || 'Permission',
          id: permissionId(item)
        })).filter((col) => col.id)

  useEffect(() => {
    Promise.all([
      scannerApi.eventsAll({ nLimit: 100 }),
      scannerApi.permissions(),
      scannerApi.roles()
    ])
      .then(([eventRes, permRes, roleRes]) => {
        setEvents(extractArray(eventRes, ['aEvent', 'aEvents']))
        setCatalog(extractArray(permRes, ['aPermission', 'aPermissions']))
        setRoles(extractArray(roleRes, ['aRole', 'aRoles']))
      })
      .catch((err) => toast.error(apiMessage(err)))
  }, [])

  useEffect(() => {
    if (!editing) return
    scannerApi
      .staffGet(staffId)
      .then((res) => {
        const data = extractData(res)
        setForm({
          sName: data?.sName || '',
          sEmail: data?.sEmail || '',
          sMobile: data?.sMobile || '',
          sCountryCode: data?.sCountryCode || '91'
        })
        const next = {}
        ;(data?.aPermissions || []).forEach((row) => {
          next[row.iEventId] = (row.aPermissionIds || []).reduce((acc, id) => ({ ...acc, [id]: true }), {})
        })
        setMatrix(next)
        const roleName = data?.aRoleId?.[0]?.sName || data?.eType
        if (roleName === 'EVENT_ADMIN') setRoleType('EVENT_ADMIN')
      })
      .catch((err) => toast.error(apiMessage(err)))
  }, [editing, staffId])

  const toggleCell = (eventId, permissionIdValue) => {
    setMatrix((prev) => {
      const row = { ...(prev[eventId] || {}) }
      row[permissionIdValue] = !row[permissionIdValue]
      return { ...prev, [eventId]: row }
    })
  }

  const buildPermissions = () =>
    Object.entries(matrix)
      .map(([iEventId, flags]) => ({
        iEventId,
        aPermissionIds: Object.keys(flags).filter((id) => flags[id])
      }))
      .filter((row) => row.aPermissionIds.length > 0)

  const resolveRoleId = () => {
    const match = roles.find((role) => (role.sName || role.sKey || '').toUpperCase().includes(roleType === 'EVENT_ADMIN' ? 'EVENT_ADMIN' : 'SCANNER'))
    return match?._id
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!form.sName.trim() || (!form.sEmail.trim() && !form.sMobile.trim())) {
      toast.error('Name and email or mobile are required.')
      return
    }
    setSaving(true)
    const roleId = resolveRoleId()
    const body = {
      sName: form.sName.trim(),
      sEmail: form.sEmail.trim() || undefined,
      sMobile: form.sMobile.trim() || undefined,
      sCountryCode: form.sCountryCode,
      aRoleId: roleId ? [roleId] : undefined,
      aPermissions: buildPermissions()
    }
    try {
      if (editing) await scannerApi.staffUpdate(staffId, body)
      else await scannerApi.staffCreate(body)
      toast.success(editing ? 'Staff updated' : 'Staff added')
      navigate('/staff')
    } catch (err) {
      toast.error(apiMessage(err))
    } finally {
      setSaving(false)
    }
  }

  if (!canManageStaff(user)) return <PermissionEmpty />

  return (
    <div className="min-h-dvh bg-bg text-ink">
      <header className="sticky top-0 z-20 flex items-center gap-2 border-b border-line bg-surface px-3 py-3">
        <button type="button" className="flex h-12 w-12 items-center justify-center rounded-2xl bg-elevated" onClick={() => navigate('/staff')}>
          <IconBack className="h-5 w-5" />
        </button>
        <h1 className="mb-0 text-xl font-bold">{editing ? 'Edit staff' : 'Add staff'}</h1>
      </header>
      <form onSubmit={onSubmit} className="mx-auto max-w-4xl space-y-5 px-4 py-5">
        <div>
          <Label>Name</Label>
          <Input value={form.sName} onChange={(e) => setForm((p) => ({ ...p, sName: e.target.value }))} />
        </div>
        <div>
          <Label>Email</Label>
          <Input type="email" value={form.sEmail} onChange={(e) => setForm((p) => ({ ...p, sEmail: e.target.value }))} />
        </div>
        <div>
          <Label>Mobile</Label>
          <Input type="tel" value={form.sMobile} onChange={(e) => setForm((p) => ({ ...p, sMobile: e.target.value }))} />
        </div>
        <div>
          <Label>Role</Label>
          <Segmented
            value={roleType}
            onChange={setRoleType}
            options={[
              { value: 'SCANNER_USER', label: 'Scanner user' },
              { value: 'EVENT_ADMIN', label: 'Event admin' }
            ]}
          />
        </div>

        <div className="card-surface overflow-x-auto p-3">
          <p className="mb-3 text-sm font-semibold">Event permissions</p>
          {events.length === 0
            ? (
            <p className="mb-0 text-sm text-muted">No events available.</p>
              )
            : (
            <table className="min-w-full text-left text-xs">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-surface p-2">Event</th>
                  {columns.map((col) => (
                    <th key={col.key} className="p-2 text-center">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event._id} className="border-t border-line">
                    <td className="sticky left-0 max-w-[140px] truncate bg-surface p-2 font-medium">{event.sTitle}</td>
                    {columns.map((col) => (
                      <td key={col.key} className="p-2 text-center">
                        <input
                          type="checkbox"
                          className="h-5 w-5 accent-theme"
                          checked={Boolean(matrix[event._id]?.[col.id])}
                          onChange={() => toggleCell(event._id, col.id)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
              )}
        </div>

        <Button className="w-full" type="submit" disabled={saving}>
          {saving ? 'Saving...' : editing ? 'Save staff' : 'Add staff'}
        </Button>
      </form>
    </div>
  )
}

export default StaffForm
